import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box, IconButton, Skeleton, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useTranslation } from "react-i18next";

import { searchProduct } from "../../../services/searchProductService";
import { Product } from "../../../types/products";
import ProductCard from "../products/ProductCard";

interface HomeProductRowProps {
  title: string;
  searchTerm: string;
  retailer?: string;
}

const brand = {
  text: "#1e293b",
  borderNeutral: "#e2e8f0",
  surface: "#ffffff",
};

const CARD_WIDTH = "clamp(170px, 24vw, 235px)";
const SKELETON_COUNT = 4;
const SKELETON_HEIGHT = 320;

const HomeProductRow: React.FC<HomeProductRowProps> = ({
  title,
  searchTerm,
  retailer = "amazon",
}) => {
  const { t: myTranslate } = useTranslation();
  const scrollContainerRef = React.useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["home-product-section", retailer, searchTerm],
    queryFn: () =>
      searchProduct({
        query: searchTerm,
        retailer,
        page: 1,
      }),
  });

  const products: Product[] = data?.data?.results ?? [];

  const checkScrollPosition = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollLeft, scrollWidth, clientWidth } = container;
    const maxScrollLeft = scrollWidth - clientWidth;

    if (maxScrollLeft <= 0) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < maxScrollLeft - 10);
  }, []);

  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const handleScroll = () => checkScrollPosition();
    container.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", checkScrollPosition);
    checkScrollPosition();

    return () => {
      container.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", checkScrollPosition);
    };
  }, [checkScrollPosition]);

  React.useEffect(() => {
    checkScrollPosition();
  }, [checkScrollPosition, products.length, isLoading]);

  const handleScroll = (direction: "left" | "right") => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.75;
    const delta = direction === "left" ? -scrollAmount : scrollAmount;
    container.scrollBy({ left: delta, behavior: "smooth" });
  };

  const renderSkeletons = () =>
    [...Array(SKELETON_COUNT)].map((_, index) => (
      <Box
        key={`home-product-skeleton-${index}`}
        sx={{
          width: CARD_WIDTH,
          flex: `0 0 ${CARD_WIDTH}`,
          scrollSnapAlign: "start",
          py: 0.5,
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height={SKELETON_HEIGHT}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    ));

  return (
    <Box sx={{ my: { xs: 4, md: 6 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          px: { xs: 1, sm: 0 },
        }}
      >
        <Typography
          variant="h5"
          className="notranslate"
          translate="no"
          sx={{ fontWeight: 800, color: brand.text, letterSpacing: "-0.5px" }}
        >
          {title}
        </Typography>

        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => handleScroll("left")}
            disabled={!canScrollLeft}
            size="small"
            aria-label={`Scroll ${title} left`}
            sx={{
              bgcolor: brand.surface,
              border: `1px solid ${brand.borderNeutral}`,
              opacity: canScrollLeft ? 1 : 0.3,
              "&:hover": { bgcolor: brand.surface, borderColor: brand.text },
              cursor: canScrollLeft ? "pointer" : "default",
            }}
          >
            <ChevronLeft />
          </IconButton>

          <IconButton
            onClick={() => handleScroll("right")}
            disabled={!canScrollRight}
            size="small"
            aria-label={`Scroll ${title} right`}
            sx={{
              bgcolor: brand.surface,
              border: `1px solid ${brand.borderNeutral}`,
              opacity: canScrollRight ? 1 : 0.3,
              "&:hover": { bgcolor: brand.surface, borderColor: brand.text },
              cursor: canScrollRight ? "pointer" : "default",
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          gap: { xs: 1.5, md: 2 },
          overflowX: "auto",
          overflowY: "visible",
          scrollBehavior: "smooth",
          scrollSnapType: "x mandatory",
          overscrollBehaviorX: "contain",
          pt: 1,
          pb: 3,
          px: { xs: 1, sm: 0 },
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {isLoading && renderSkeletons()}

        {!isLoading && error && (
          <Typography color="error" className="notranslate" translate="no">
            {myTranslate("products.unableToLoadProducts")}
          </Typography>
        )}

        {!isLoading && !error && products.length === 0 && (
          <Typography
            color="text.secondary"
            className="notranslate"
            translate="no"
          >
            {myTranslate("products.noProductsFound", { searchTerm })}
          </Typography>
        )}

        {!isLoading &&
          !error &&
          products.map((product, index) => {
            const isLastItem = index === products.length - 1;
            const snapAlign = isLastItem ? "end" : "start";

            return (
              <Box
                key={`${product.product_id}-${index}`}
                sx={{
                  width: CARD_WIDTH,
                  flex: `0 0 ${CARD_WIDTH}`,
                  py: 0.5,
                  scrollSnapAlign: snapAlign,
                }}
              >
                <ProductCard
                  product={product}
                  retailer={retailer}
                  index={index}
                />
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

const HomeProductSection: React.FC = () => {
  const { t: myTranslate } = useTranslation();
  const sections = [
    {
      titleKey: "home.sections.bestSellers",
      searchTerm: "most popular cosmetics",
    },
    {
      titleKey: "home.sections.topLaptops",
      searchTerm: "surface pro laptop",
    },
    {
      titleKey: "home.sections.topSellerVitamins",
      searchTerm: "top seller vitamins",
    },
    {
      titleKey: "home.sections.topSellerClothesShoes",
      searchTerm:
        'puma|nike|adidas|reebok|under armour "t-shirt" OR tshirt OR shoes',
    },
  ];

  return (
    <Box
      sx={{
        maxWidth: { xs: "100%", lg: 1200 },
        mx: "auto",
        pb: 6,
      }}
    >
      {sections.map((section) => (
        <HomeProductRow
          key={section.titleKey}
          title={myTranslate(section.titleKey)}
          searchTerm={section.searchTerm}
          retailer="amazon"
        />
      ))}
    </Box>
  );
};

export default HomeProductSection;
